import { FooterProps } from '@awazevr/design-system.compositions.footer'
import { GlobalProps } from 'src/utils/globalProps'
import { useContext } from 'react'
import { useIntl } from 'react-intl'

type Location = FooterProps['locationSelect']['currentLocation']

export const useLocations = (): FooterProps['locationSelect'] => {
  const { formatMessage } = useIntl()
  const { brand, country, currency } = useContext(GlobalProps)

  const locationMap: Record<
    string,
    Record<string, Location & { localisedName: string }>
  > = {
    novasol: {
      DK: {
        name: 'Denmark',
        displayName: 'Dansk — DKK',
        href: 'https://www.novasol.dk',
        localisedName: formatMessage({
          id: 'footer.locations.location-Denmark',
          defaultMessage: 'Denmark',
        }),
      },
      DE: {
        name: 'Germany',
        displayName: 'Deutsch — EUR',
        href: 'https://www.novasol.de',
        localisedName: formatMessage({
          id: 'footer.locations.location-Germany',
          defaultMessage: 'Germany',
        }),
      },
      AT: {
        name: 'Austria',
        displayName: 'Deutsch — EUR',
        href: 'https://www.novasol.at',
        localisedName: formatMessage({
          id: 'footer.locations.location-Austria',
          defaultMessage: 'Austria',
        }),
      },
      EU: {
        name: 'Europe',
        displayName: 'English — EUR',
        href: 'https://www.novasol.com',
        localisedName: formatMessage({
          id: 'footer.locations.location-Europe',
          defaultMessage: 'Europe',
        }),
      },
      GB: {
        name: 'Uk',
        displayName: 'English — GBP',
        href: 'https://www.novasol.co.uk',
        localisedName: formatMessage({
          id: 'footer.locations.location-Uk',
          defaultMessage: 'Uk',
        }),
      },
      US: {
        name: 'Usa',
        displayName: 'English — USD',
        href: 'https://www.novasol.us',
        localisedName: formatMessage({
          id: 'footer.locations.location-Usa',
          defaultMessage: 'Usa',
        }),
      },
      ES: {
        name: 'Spain',
        displayName: 'Español — EUR',
        href: 'https://www.novasol-vacaciones.es',
        localisedName: formatMessage({
          id: 'footer.locations.location-Spain',
          defaultMessage: 'Spain',
        }),
      },
      FR: {
        name: 'France',
        displayName: 'Français — EUR',
        href: 'https://www.novasol-vacances.fr',
        localisedName: formatMessage({
          id: 'footer.locations.location-France',
          defaultMessage: 'France',
        }),
      },
      HR: {
        name: 'Croatia',
        displayName: 'Hrvatski — EUR',
        href: 'https://www.novasol.hr',
        localisedName: formatMessage({
          id: 'footer.locations.location-Croatia',
          defaultMessage: 'Croatia',
        }),
      },
      IT: {
        name: 'Italy',
        displayName: 'Italiano — EUR',
        href: 'https://www.novasol.it',
        localisedName: formatMessage({
          id: 'footer.locations.location-Italy',
          defaultMessage: 'Italy',
        }),
      },
      NL: {
        name: 'Netherlands',
        displayName: 'Nederlands — EUR',
        href: 'https://www.novasol.nl',
        localisedName: formatMessage({
          id: 'footer.locations.location-Netherlands',
          defaultMessage: 'Netherlands',
        }),
      },
      NO: {
        name: 'Norway',
        displayName: 'Norsk — NOK',
        href: 'https://www.novasol.no',
        localisedName: formatMessage({
          id: 'footer.locations.location-Norway',
          defaultMessage: 'Norway',
        }),
      },
      PL: {
        name: 'Poland',
        displayName: 'Polski — PLN',
        href: 'https://www.novasol.pl',
        localisedName: formatMessage({
          id: 'footer.locations.location-Poland',
          defaultMessage: 'Poland',
        }),
      },
      SE: {
        name: 'Sweden',
        displayName: 'Svenska — SEK',
        href: 'https://www.novasol.se',
        localisedName: formatMessage({
          id: 'footer.locations.location-Sweden',
          defaultMessage: 'Sweden',
        }),
      },
    },
    fincallorca: {
      DE: {
        name: 'Germany',
        displayName: 'Deutsch — EUR',
        href: 'https://www.fincallorca.de',
        localisedName: formatMessage({
          id: 'footer.locations.location-Germany',
          defaultMessage: 'Germany',
        }),
      },
      ES: {
        name: 'Spain',
        displayName: 'Español — EUR',
        href: 'https://www.fincallorca.es',
        localisedName: formatMessage({
          id: 'footer.locations.location-Spain',
          defaultMessage: 'Spain',
        }),
      },
    },
  }

  if (locationMap[brand] === undefined) {
    return
  }

  const currentLocation =
    locationMap[brand][country] || locationMap['novasol'].EU

  return {
    availableLocations: Object.values(locationMap[brand]).sort(
      (
        a: Location & { localisedName: string },
        b: Location & { localisedName: string },
      ) => a.name.localeCompare(b.name),
    ),
    currentLocation,
    closeLocationButtonTitle: formatMessage({
      id: 'footer.location-selector.close',
      defaultMessage: 'Close location selector',
    }),
    currentLocationSummary: formatMessage(
      {
        id: 'footer.location-selector.summary',
        defaultMessage:
          'Your selected location is {location} and currency will be in {currency}',
      },
      {
        location: currentLocation.localisedName,
        currency: currency?.toLocaleUpperCase(),
      },
    ),
    heading: formatMessage({
      id: 'footer.location-selector.heading',
      defaultMessage: 'Select your location',
    }),
  }
}
